import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { HTML_PAGE_BLOCK_TYPE } from "../../../cores/websitePage/constants/htmlPageBlock";
import { WebsitePageContent } from "../../../cores/websitePage/interfaces/WebsiteBlock";
import { BlockRenderer } from "./BlockRenderer";
import { HTML_PREVIEW_SANDBOX, prepareHtmlPreviewDocument } from "./htmlPreview.utils";
import { WebsiteIcon } from "./WebsiteIcon";
import "./MiniPagePreview.scss";

interface Props {
  content: WebsitePageContent;
  className?: string;
}

export function MiniPagePreview({ content, className }: Props) {
  const intl = useIntl();
  const htmlPageBlock = content?.blocks?.find((block) => block.type === HTML_PAGE_BLOCK_TYPE);
  const legacyHtml = content?.mode === "html" ? content.html : undefined;
  const htmlSource = htmlPageBlock?.html || legacyHtml;

  const previewDoc = useMemo(
    () => (htmlSource ? prepareHtmlPreviewDocument(htmlSource) : ""),
    [htmlSource]
  );

  if (htmlSource) {
    return (
      <div className={`wp-mini-preview ${className || ""}`}>
        <iframe
          title={intl.formatMessage({ id: "app.websitePage.preview.miniFrame" })}
          className="wp-mini-preview__html-frame"
          srcDoc={previewDoc}
          sandbox={HTML_PREVIEW_SANDBOX}
        />
      </div>
    );
  }

  const blocks = content?.blocks || [];

  if (blocks.length === 0) {
    return (
      <div className={`wp-mini-preview wp-mini-preview--empty ${className || ""}`}>
        <div className="wp-mini-preview__placeholder">
          <WebsiteIcon icon="layer-group" />
          <span>{intl.formatMessage({ id: "app.websitePage.preview.noContent" })}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`wp-mini-preview ${className || ""}`}>
      <div className="wp-mini-preview__viewport">
        <div className="wp-mini-preview__scale">
          {blocks.slice(0, 6).map((block) => (
            <BlockRenderer key={block.id} block={block} />
          ))}
        </div>
      </div>
    </div>
  );
}
